import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import BrandSelect from '../ui/BrandSelect';
import { adminToolbarSelect } from '../utils/ReactSelectStyles';
import { Actions as ORG_ACTIONS } from '../../store/actions/organisationActions';
import { Actions as DASHBOARD_ACTIONS } from '../../store/actions/consultantDashboardActions';
import { toggleAlertMessage } from '../../store/action';
import {
  billingRelationshipLabel,
  getClientUnusableReason,
  getConsultantClientAccessLockMessage,
  isClientUsable,
  planTypeLabel,
  resolveClientPriceLabel,
  resolveCommitmentEndLabel,
  statusBadgeConfig,
} from '../utils/consultantClientUtils';
import { firmUserOwnerOptions } from './ownerDisplay';

const ConsultantClientDetail = ({
  clients,
  renewals,
  schedules,
  masterPerReportFeeVal,
  onBack,
  setCurrentOrganisation,
  updateOrganisation,
  getOrganisation,
  organisationUsers,
  getOrganisationUsers,
  assignClientOwner,
  getClientHealth,
  dashboardClients,
  ownerLoading,
  showAlert,
}) => {
  const { id } = useParams();
  const history = useHistory();
  const client = clients.find((c) => c.id === id);
  const renewal = (renewals || []).find((r) => r.organisationId === id);
  const schedule = (schedules || []).find((s) => s.organisationId === id);
  const currency = client?.currency || '£';

  const [ownerUserId, setOwnerUserId] = useState(null);
  const [ownerReady, setOwnerReady] = useState(false);

  useEffect(() => {
    getOrganisationUsers();
  }, [getOrganisationUsers]);

  useEffect(() => {
    let cancelled = false;
    const fromDash = (dashboardClients || []).find((c) => String(c.clientOrgId) === String(id));
    if (fromDash) {
      setOwnerUserId(fromDash.ownerUserId || null);
      setOwnerReady(true);
      return undefined;
    }
    ;(async () => {
      const result = await getClientHealth(id);
      if (cancelled) return;
      if (result?.ok) {
        setOwnerUserId(result.data?.ownerUserId || null);
      }
      setOwnerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, dashboardClients, getClientHealth]);

  const ownerOptions = useMemo(() => firmUserOwnerOptions(organisationUsers), [organisationUsers]);
  const selectedOwner =
    ownerOptions.find((o) => o.value === (ownerUserId || '')) || ownerOptions[0];

  if (!client) {
    return (
      <div className='flex flex-col gap-4'>
        <Button variant='ghost' size='sm' onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to portfolio
        </Button>
        <p className='text-muted-foreground'>Client not found.</p>
      </div>
    );
  }

  const statusCfg = statusBadgeConfig(client);
  const usable = isClientUsable(client, renewal);
  const unusableReason = getClientUnusableReason(client);
  const accessLockMessage = getConsultantClientAccessLockMessage(client);

  const handleOpenOrg = () => {
    updateOrganisation(client);
    setCurrentOrganisation(client);
    localStorage.setItem('Organisation', client.id);
    localStorage.setItem('orgDetails', JSON.stringify(client));
    getOrganisation();
    history.push('/');
  };

  const handleOwnerChange = async (opt) => {
    const next = opt?.value || null;
    const result = await assignClientOwner(id, next || null);
    if (result?.ok) {
      setOwnerUserId(next);
      showAlert(true, next ? 'Account manager updated.' : 'Account manager cleared.', 'success', 4000);
    } else {
      showAlert(true, result?.message || 'Could not update account manager.', 'error', 5000);
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center gap-3'>
        <Button variant='ghost' size='sm' onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={1.5} />
        </Button>
        <div className='flex-1'>
          <h2 className='text-lg font-semibold flex items-center gap-2 text-foreground'>
            <Building2 size={20} strokeWidth={1.5} />
            {client.name || client.accountName}
          </h2>
          <p className='text-sm text-muted-foreground'>Client billing and access summary</p>
        </div>
        <Button size='sm' onClick={handleOpenOrg}>
          <ExternalLink size={14} strokeWidth={1.5} />
          Open client workspace
        </Button>
      </div>

      {accessLockMessage && (
        <Alert variant='warning'>
          <AlertDescription className='text-foreground'>{accessLockMessage}</AlertDescription>
        </Alert>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-base'>Lifecycle</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            <div className='flex justify-between items-center'>
              <span className='text-muted-foreground'>Status</span>
              <Badge className={statusCfg.className}>{statusCfg.label}</Badge>
            </div>
            <div className='flex justify-between'>
              <span className='text-muted-foreground'>Billing</span>
              <span className='text-foreground'>{billingRelationshipLabel(client.billingRelationship)}</span>
            </div>
            <div className='flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center'>
              <span className='text-muted-foreground shrink-0'>Account manager</span>
              <div className='min-w-[200px] w-full sm:w-64'>
                <BrandSelect
                  styles={adminToolbarSelect}
                  options={ownerOptions}
                  value={selectedOwner}
                  onChange={handleOwnerChange}
                  placeholder={ownerReady ? 'Select team member' : 'Loading…'}
                  isDisabled={!ownerReady || !!ownerLoading}
                  isSearchable
                />
              </div>
            </div>
            {client.billingRelationship === 'direct_billed' && (
              <div className='flex justify-between'>
                <span className='text-muted-foreground'>Subscription active</span>
                <span className='text-foreground'>{client.hasSubscriptionActive ? 'Yes' : 'No'}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className='bg-card'>
          <CardHeader>
            <CardTitle className='text-base'>Plan & renewal</CardTitle>
          </CardHeader>
          <CardContent className='space-y-3 text-sm'>
            {schedule || renewal ? (
              <>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Plan type</span>
                  <span className='text-foreground'>{planTypeLabel(schedule?.planType || renewal?.planType, client)}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Price</span>
                  <span className='text-foreground'>
                    {resolveClientPriceLabel(client, schedule || renewal, masterPerReportFeeVal, currency)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Commitment ends</span>
                  <span className='text-foreground'>
                    {renewal?.commitmentEndDate || resolveCommitmentEndLabel(client, schedule)}
                  </span>
                </div>
                {renewal?.daysUntilCommitmentEnd != null && (
                  <div className='flex justify-between'>
                    <span className='text-muted-foreground'>Days until end</span>
                    <span className='text-foreground'>{renewal.daysUntilCommitmentEnd}</span>
                  </div>
                )}
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Auto-renew</span>
                  <span className='text-foreground'>{(schedule || renewal)?.autoRenew !== false ? 'Yes' : 'No'}</span>
                </div>
              </>
            ) : (
              <p className='text-muted-foreground'>
                No billing schedule on file.{' '}
                {!client.consultantClientScheduleId &&
                  'Use Onboard existing in the portfolio or contact CYF admin to set commitment dates.'}
              </p>
            )}
          </CardContent>
        </Card>

        {!usable && (
          <Card className='bg-card md:col-span-2'>
            <CardHeader>
              <CardTitle className='text-base'>Report access</CardTitle>
            </CardHeader>
            <CardContent>
              <p className='text-warning text-sm'>{unusableReason}</p>
              {client.billingRelationship === 'consultant_billed' &&
                (schedule?.planType === 'per_report' ||
                  renewal?.planType === 'per_report' ||
                  !client.billingRelationship) && (
                  <p className='text-muted-foreground text-xs mt-2'>
                    Per-report fees are billed to your consultant account when reports are generated.
                  </p>
                )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  organisationUsers: state.organisation?.users || [],
  dashboardClients: state.consultantDashboard?.dashboard?.clients || [],
  ownerLoading: state.consultantDashboard?.loading?.owner,
});

const mapDispatchToProps = (dispatch) => ({
  setCurrentOrganisation: (organisation) => dispatch(ORG_ACTIONS.setCurrentOrganisation(organisation)),
  updateOrganisation: (organisation) => dispatch(ORG_ACTIONS.updateOrganisation(organisation)),
  getOrganisation: () => dispatch(ORG_ACTIONS.getOrganisation()),
  getOrganisationUsers: () => dispatch(ORG_ACTIONS.getOrganisationUsers()),
  assignClientOwner: (orgId, ownerUserId) => dispatch(DASHBOARD_ACTIONS.assignClientOwner(orgId, ownerUserId)),
  getClientHealth: (orgId) => dispatch(DASHBOARD_ACTIONS.getClientHealth(orgId)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsultantClientDetail);
